import React, { useState } from 'react'
import { useQueryClient } from 'react-query'
import { AccountApi, CreateUser, Member, SuccessResponse, User } from '@citruscamps/citrus-client'
import { ROUTES } from '../../../../../constants/routes'
import { useAuth } from '../../../../../hooks/useAuth'
import { useRouter } from '../../../../../hooks/useRouter'
import { sendEvent } from '../../../../../lib/gtag'
import translations from '../../../../../translations/en.json'
import { generateApiConfig } from '../../../../../utils/client-config'
import { useRequestHandler } from '../../../../../hooks/useRequestHandler'

interface ISignupUser {
  user?: User
  member?: Member
  isLoading: boolean
  error: React.ReactElement | undefined
  handleCreate: (inputs: CreateUser) => Promise<void>
  handleVerifyEmail: (code: string) => Promise<void>
  handleResendVerifyEmail: () => Promise<void>
  setError: React.Dispatch<React.SetStateAction<React.ReactElement | undefined>>
}

export const useSignupUser = (): ISignupUser => {
  const { requestHandler } = useRequestHandler()
  const { user, member, isLoading, handleSignUp, setUser } = useAuth()
  const queryClient = useQueryClient()
  const [error, setError] = useState<React.ReactElement | undefined>()
  const { location, query, ...router } = useRouter()
  const handleCreate = async (inputs: CreateUser): Promise<void> => {
    try {
      const accountClient = new AccountApi(generateApiConfig())
      let _user: User | undefined = user
      if (!_user) {
        _user = await handleSignUp(inputs)
      } else {
        _user = await requestHandler<User>(() =>
          accountClient.updateCurrentUserAccount({
            User: {
              ...user,
              first_name: inputs.first_name,
              last_name: inputs.last_name,
              email: inputs.email,
              phone: inputs.phone,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            } as User,
          }),
        )
        await handleResendVerifyEmail()
      }
      // TODO: Re-enable signup tracking (disabled due to "/" appended to body)
      // sendEvent({
      //   label: 'Next',
      //   category: 'Lead',
      //   method: 'email',
      //   step: 'create_account',
      //   action: 'sign_up',
      // })
      await setUser(_user)
    } catch (e: any) {
      setError(e.message || (translations as any)['error.default_toast_message'])
      throw e
    }
  }

  const handleVerifyEmail = async (code: string) => {
    try {
      const accountClient = new AccountApi(generateApiConfig())
      await requestHandler<User>(
        () => accountClient.verifyEmailAccount({ EmailVerification: { code } }),
        {
          redirect: false,
        },
      )
      await queryClient.refetchQueries('user')
      sendEvent({
        category: 'Lead',
        method: 'email',
        step: 'verify_email',
        action: 'sign_up',
      })
      await router.push({ pathname: ROUTES.SETUP, search: location.search })
    } catch (e: any) {
      const message = e.message || (translations as any)['error.default_toast_message']
      setError(message)
    }
  }

  const handleResendVerifyEmail = async () => {
    try {
      const accountClient = new AccountApi(generateApiConfig())
      await requestHandler<SuccessResponse>(() => accountClient.reverifyEmailAccount(), {
        redirect: false,
      })
    } catch (e: any) {
      const message = e.message || (translations as any)['error.default_toast_message']
      setError(message)
    }
  }

  return {
    user,
    member,
    isLoading,
    error,
    handleCreate,
    handleVerifyEmail,
    handleResendVerifyEmail,
    setError,
  }
}
